/**
 * Changelog Version Classifier
 *
 * Encapsulates the rules for how changelog versions are categorised in the
 * "What's New" dialog:
 *   - Latest release (any type) → featured prominently at the top
 *   - Minor/patch releases before the previous major → "Recent Build Updates" (collapsible)
 *   - Major milestone (x.0.0) and older → "Version History" (collapsible, capped)
 */

/** Parsed version entry from the user-facing changelog */
export interface VersionInfo {
  /** Semantic version string (e.g., "1.2.3") */
  version: string
  /** Release date in human-readable format */
  date: string
  /** List of new features */
  added: string[]
  /** List of changes/improvements */
  changed: string[]
  /** List of bug fixes */
  fixed: string[]
}

/** Result of grouping a version list for the What's New dialog */
export interface VersionGroups {
  /** Most recent release — always shown at the top */
  featured: VersionInfo | null
  /** Minor/patch releases between featured and the previous major boundary */
  recentBuilds: VersionInfo[]
  /** Previous major milestone and older releases (capped) */
  older: VersionInfo[]
}

/** Maximum number of older versions to render in the Version History section */
const MAX_OLDER_VERSIONS = 10

/**
 * Returns true only for true semver major releases (x.0.0).
 * Both the minor and patch segments must be zero.
 *
 * @example
 * isMajorRelease('2.0.0') // true
 * isMajorRelease('1.3.0') // false — minor release
 * isMajorRelease('1.3.2') // false — patch release
 */
export function isMajorRelease(version: string): boolean {
  const parts = version.split('.')
  if (parts.length !== 3 || parts.some(p => !/^\d+$/.test(p))) return false
  return Number(parts[1]) === 0 && Number(parts[2]) === 0
}

/**
 * Groups a newest-first list of versions into the three dialog sections.
 *
 * Layout rules:
 * - `featured`     — always `versions[0]` (most recent, regardless of type)
 * - `recentBuilds` — all entries after featured up to (but not including) the
 *                    first major milestone in the remaining list
 * - `older`        — from that major milestone onward, capped at MAX_OLDER_VERSIONS
 *
 * @param versions Parsed version list, newest-first
 */
export function groupVersions(versions: VersionInfo[]): VersionGroups {
  if (versions.length === 0) return { featured: null, recentBuilds: [], older: [] }

  const featured = versions[0] as VersionInfo
  const rest = versions.slice(1)

  const boundaryIdx = rest.findIndex(v => isMajorRelease(v.version))
  if (boundaryIdx === -1) {
    return { featured, recentBuilds: rest, older: [] }
  }

  return {
    featured,
    recentBuilds: rest.slice(0, boundaryIdx),
    older: rest.slice(boundaryIdx, boundaryIdx + MAX_OLDER_VERSIONS),
  }
}
